// Pure formatters for the History sheet's per-version rows.
// No DOM, no React, no fetch — all input/output is plain JS values
// so the unit tests stay fast and the React layer stays thin.

export function shortSha(sha256) {
  if (!sha256) return '—';
  return sha256.slice(0, 7);
}

export function isAgentCommitter(committed_by) {
  if (!committed_by) return false;
  return committed_by.startsWith('mxs_');
}

// Returns the version_id of the single current (newest live) version, or null.
// `versions` is the API array, newest-first (version_id DESC). "Current" is the
// newest non-tombstone version — NOT content identity (sha256), because moves
// and no-op writes reuse the prior content's sha256 across many versions.
export function currentVersionId(versions) {
  if (!Array.isArray(versions)) return null;
  for (const v of versions) {
    if (v.kind !== 'delete') return v.version_id;
  }
  return null;
}

export function authorClass(v, currentUser) {
  if (v.kind === 'restore' && !v.committed_by) return 'is-system';
  if (isAgentCommitter(v.committed_by)) return 'is-agent';
  if (currentUser && v.committed_by === currentUser) return 'is-user';
  return 'is-user';
}

// Render-ready net-delta descriptor. The versions API exposes only line_count
// per version (no add/remove split), so we report a single-signed net delta —
// never a misleading "+0 −0". kind: 'add' | 'remove' | 'none' | 'tombstone'.
export function lineDeltaLabel(v, parent) {
  if (v.kind === 'delete') {
    const n = parent && parent.line_count != null ? parent.line_count : null;
    return { kind: 'tombstone', n };
  }
  if (v.line_count == null) return { kind: 'none', n: 0 };
  if (parent == null) return { kind: 'add', n: v.line_count };
  if (parent.line_count == null) return { kind: 'none', n: 0 };
  const net = v.line_count - parent.line_count;
  if (net > 0) return { kind: 'add', n: net };
  if (net < 0) return { kind: 'remove', n: -net };
  return { kind: 'none', n: 0 };
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Compact absolute timestamp: "14:22" for today, "May 19" within the year,
// "May 19 2025" cross-year. `now` is injected for deterministic tests.
export function compactTimestamp(committed_at, now) {
  const d = new Date(committed_at);
  const n = new Date(now);
  const sameDay = d.getFullYear() === n.getFullYear()
    && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
  if (sameDay) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  }
  const base = `${MONTHS[d.getMonth()]} ${d.getDate()}`;
  return d.getFullYear() === n.getFullYear() ? base : `${base} ${d.getFullYear()}`;
}

export function relativeAgo(then, now) {
  const diff = Math.max(0, now - then);
  const min = 60_000;
  const hr = 60 * min;
  const day = 24 * hr;
  if (diff < min) return 'just now';
  if (diff < hr) return `${Math.floor(diff / min)} min ago`;
  if (diff < day) return `${Math.floor(diff / hr)} h ago`;
  if (diff < 2 * day) return 'yesterday';
  if (diff < 7 * day) return `${Math.floor(diff / day)} d ago`;
  return compactTimestamp(then, now);
}

export function formatHistoryRow(v, parent, now, currentUser) {
  const isTombstone = v.kind === 'delete';
  return {
    versionId:   v.version_id,
    timestamp:   compactTimestamp(v.committed_at, now),
    ago:         relativeAgo(v.committed_at, now),
    shortSha:    shortSha(v.sha256),
    authorName:  v.committed_by ?? '—',
    authorClass: authorClass(v, currentUser),
    delta:       lineDeltaLabel(v, parent),
    isTombstone,
    kind:        v.kind,
    snippet:     isTombstone ? 'file deleted' : (v.note ?? ''),
    parentVersionId: v.parent_version_id,
  };
}
